/**
 * AuthContext — stores admin JWT in memory only (NOT sessionStorage/localStorage).
 *
 * Design choice (per user spec): the token lives in React state. A page refresh
 * forces re-login, which is the desired behavior for an admin-only area.
 *
 * Exposes:
 *   - token: string | null
 *   - login(username, password): Promise<void>  -> throws on failure
 *   - logout(): void
 *   - authFetch(input, init): Promise<Response>  -> auto-attaches Bearer + handles 401
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [username, setUsername] = useState(null);

  const login = useCallback(async (user, pass) => {
    const res = await fetch(`${BACKEND_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: user, password: pass }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const detail = data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Error al iniciar sesión';
      throw new Error(msg);
    }
    setToken(data.token);
    setUsername(data.username);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUsername(null);
  }, []);

  const authFetch = useCallback(async (input, init = {}) => {
    const headers = new Headers(init.headers || {});
    if (token) headers.set('Authorization', `Bearer ${token}`);
    const res = await fetch(input, { ...init, headers });
    if (res.status === 401) {
      // Token expired or invalid -> force logout so UI shows login screen
      setToken(null);
      setUsername(null);
    }
    return res;
  }, [token]);

  const value = useMemo(
    () => ({ token, username, isAuthenticated: !!token, login, logout, authFetch }),
    [token, username, login, logout, authFetch]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
